/**
 * Sign-in and account creation (architecture §6): Google, and email +
 * password for people without a Google account.
 */

import { httpClient } from './client'
import type { Locale, Plan } from '../types/domain'

/** Who the stored token belongs to, per the server (`GET /api/auth/session`). */
export interface SessionUser {
  id: string
  email: string
  locale: Locale
  profile_complete: boolean
  /** PIVOT-170. No upgrade flow yet -- rendered, at most, as a label. */
  plan: Plan
  /**
   * PIVOT-347. Whether this account has a stored password, so the profile page
   * can offer "Change password" only to someone who has one. The server sends
   * it (`auth.py::_session_user`); never infer it from a Google sign-in --
   * a Google account can hold a password too, and that proxy fails silently.
   */
  has_password: boolean
}

/**
 * Reads the session from the server rather than from the stored token
 * (PIVOT-080). Two facts the client must not decide for itself: whether the
 * token is still valid, and whether onboarding is finished. A `401` here means
 * "sign in again"; anything the client cached about either can be wrong.
 */
export function getSession(): Promise<SessionUser> {
  return httpClient.get<SessionUser>('/auth/session')
}

/** What every way in returns (Google, signup, login) — the backend answers all
 * three with one model, and `AuthContext` handles them identically. */
export interface SignInResponse {
  access_token: string
  token_type: 'bearer'
  user: SessionUser
}

/** Sends the one-time authorization code from the Google popup; the backend
 * exchanges it with Google server-side (keeping the client secret off the
 * browser) and returns our session. */
export function signInWithGoogleCode(code: string): Promise<SignInResponse> {
  return httpClient.post<SignInResponse>('/auth/google', { code })
}

export function signup(email: string, password: string, locale: Locale): Promise<SignInResponse> {
  return httpClient.post<SignInResponse>('/auth/signup', { email, password, locale })
}

export function login(email: string, password: string): Promise<SignInResponse> {
  return httpClient.post<SignInResponse>('/auth/login', { email, password })
}

/** Always resolves, regardless of whether the email is registered — the
 * backend itself never reveals that either (no user-enumeration). */
export function forgotPassword(email: string): Promise<void> {
  return httpClient.post<void>('/auth/forgot-password', { email })
}

/** Resolves with a fresh session (PIVOT-113): resetting the password also
 * revokes every session token issued before this call, this device's
 * previous one included, so the backend hands back a new one to keep going
 * with. */
export function resetPassword(token: string, newPassword: string): Promise<SignInResponse> {
  return httpClient.post<SignInResponse>('/auth/reset-password', {
    token,
    new_password: newPassword,
  })
}

/** Ends every session for the signed-in user, including the one this very
 * call is authenticated with (`POST /api/auth/logout-all`, PIVOT-113) — the
 * "sign out everywhere" action on the profile page. Resolves on the
 * backend's `204 No Content`; the caller is responsible for clearing its own
 * stored token afterwards (`clearToken()`, `auth/session.ts`), the same
 * contract `deleteAccount` has. */
export function logoutAll(signal?: AbortSignal): Promise<void> {
  return httpClient.post<void>('/auth/logout-all', undefined, signal)
}
